import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { WizardConfig } from '../types/wizard'

export default function PreviewPage() {
  const navigate = useNavigate()
  const [config, setConfig] = useState<WizardConfig | null>(null)

  useEffect(() => {
    const stored = sessionStorage.getItem('wizardConfig')
    if (stored) {
      setConfig(JSON.parse(stored))
    } else {
      // No config found, redirect to configure
      navigate('/configure')
    }
  }, [navigate])

  if (!config) {
    return <div>Loading...</div>
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <section className="section bg-white flex-1">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Preview Your Chatbot
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Here's how your chatbot will look and behave on your website.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Preview Panel */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Live Preview</h2>

                {/* Chatbot Demo */}
                <div className="bg-white rounded-xl shadow-xl p-6 border-4" style={{ borderColor: config.primaryColor }}>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div
                        className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: config.primaryColor }}
                      >
                        AI
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-lg p-3">
                        <p className="text-sm">Hi! I'm here to help you find your perfect home. Are you looking to buy or sell?</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 flex-row-reverse">
                      <div className="w-10 h-10 rounded-full bg-gray-300 flex-shrink-0"></div>
                      <div
                        className="flex-1 rounded-lg p-3 text-white"
                        style={{ backgroundColor: config.primaryColor }}
                      >
                        <p className="text-sm">I'm looking to buy!</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <div
                        className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: config.primaryColor }}
                      >
                        AI
                      </div>
                      <div className="flex-1 bg-gray-100 rounded-lg p-3">
                        <p className="text-sm">Great! What's your price range?</p>
                        <div className="mt-2 space-y-1">
                          {config.priceRanges.slice(0, 3).map(range => (
                            <button
                              key={range}
                              className="block w-full text-left px-3 py-2 text-sm rounded hover:bg-gray-200"
                            >
                              {range}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 text-center">
                      Powered by HellaBots • 24/7 Lead Capture
                    </p>
                  </div>
                </div>

                <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    <strong>✓ Your chatbot is configured!</strong><br />
                    It will automatically qualify leads and book appointments to your Calendly.
                  </p>
                </div>
              </div>

              {/* Configuration Summary */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Settings</h2>

                <div className="space-y-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Agent: {config.agentName}</h3>
                    <p className="text-sm text-gray-600">{config.email}</p>
                    <p className="text-sm text-gray-600">{config.phone}</p>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Calendly Connected</h3>
                    <p className="text-sm text-gray-600 break-all">{config.calendlyUrl}</p>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Market Coverage</h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {config.cities.map(city => (
                        <span key={city} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                          {city}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {config.priceRanges.map(range => (
                        <span key={range} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                          {range}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Branding</h3>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded border border-gray-300"
                        style={{ backgroundColor: config.primaryColor }}
                      />
                      <span className="text-sm text-gray-600">{config.primaryColor}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-6 bg-primary-50 border-2 border-primary-200 rounded-lg p-6">
                  <h3 className="font-semibold text-primary-900 mb-2">Ready to Go Live?</h3>
                  <p className="text-sm text-primary-800 mb-4">
                    Complete payment and you'll receive your embed code instantly. Add it to your website in 2 minutes.
                  </p>
                  <div className="flex gap-3">
                    <Link
                      to="/configure"
                      className="flex-1 px-4 py-2 rounded-lg font-semibold bg-white text-primary-600 border-2 border-primary-600 hover:bg-primary-50 text-center"
                    >
                      Edit Settings
                    </Link>
                    <Link
                      to="/checkout"
                      className="flex-1 btn-primary text-center"
                    >
                      Proceed to Checkout
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
